import {AccountNumber} from "../../account/types/SimpleAccountTypes";
import {Exchange} from "../../instruments/exchange/Exchange";
import {CodeAndExchange} from "../../instruments/types/CodeAndExchange";
import {isIressOrder} from "./iress/IressOrderRecord";
import {PricingInstruction} from "./PricingInstruction";
import {OffsetDateTime} from "../../pseudo-types/DateTypes";
import {OrderConditions} from "./OrderConditions";

export type OrderNumber = number & { __orderNumber: number };

export type OrderNumbers = ReadonlyArray<OrderNumber>;

export type OrderRecords = ReadonlyArray<OrderRecord>;

export interface OrderRecord extends CodeAndExchange {
    readonly orderNumber: OrderNumber;
    readonly accountNumber: AccountNumber;
    readonly side: OrderSide;
    readonly destination: OrderDestination;
    readonly priceInstruction: PricingInstruction;
    readonly status: OrderStatus;
    readonly state: OrderState;
    readonly lastAction: string;
    readonly actionStatus: string;
    readonly stateDescription: string;
    readonly price: number;
    readonly quantity: number;
    readonly remainingQuantity: number;
    readonly doneQuantity: number;
    readonly doneValue: number;
    readonly averagePrice: number;
    readonly lifetime: OrderLifetime;
    readonly expiry: OffsetDateTime | null;
    readonly created: OffsetDateTime;
    readonly updated: OffsetDateTime;
    readonly conditions: OrderConditions | null;
    readonly parentOrderNumber: OrderNumber;
    readonly uncommittedVolume: number;
    readonly parentOrderLimit: number;
}

export interface TmcOrderRecord extends OrderRecord {
    readonly legs: ReadonlyArray<TmcLeg>;
}

export interface TmcLeg extends CodeAndExchange {
    readonly side: OrderSide;
    readonly ratio: number;
}

export enum IosServerName {
    AUSIEX = "AUSIEX",
    PHILLIP = "PHILLIP",
    MORRISON = "MORRISON",
    MORRISON_TEST = "MORRISON_TEST",
    D2MX = "D2MX",
    SHAW = "SHAW",
    PERSHING = "PERSHING",
    OM = "OM",
    OM_TEST = "OM_TEST",
    IRESS_DEMO = "IRESS_DEMO",
    PHILLIP_TEST = "PHILLIP_TEST",
    AUSIEX_TEST = "AUSIEX_TEST",
    CBA_TEST = "CBA_TEST",
}

export enum OrderSide {
    BUY = "BUY",
    LONG_SELL = "LONG_SELL",
    SHORT_SELL = "SHORT_SELL",
    SHORT_EXEMPT = "SHORT_EXEMPT",
    UNDETERMINED = "UNDETERMINED",
    CROSS = "CROSS",
    CROSS_SHORT = "CROSS_SHORT",
    CROSS_SHORT_EX = "CROSS_SHORT_EX",
    MULTI_AS_DEFINED = "MULTI_AS_DEFINED",
    MULTI_OPPOSITE = "MULTI_OPPOSITE"
}

export enum OrderDestination {
    DESK = "DESK",
    ASX = "ASX",
    ASXCP = "ASXCP",
    CXA = "CXA",
    BESTMKT = "BESTMKT",
    AUTODESK = "AUTODESK",
    FIXED_CO = "FIXED_CO",
    IFDONE = "IFDONE",
    OCO = "OCO",
    BPO = "BPO",
    CXAMID = "CXAMID",
    Q_CXA = "Q_CXA",
    UNKNOWN = "UNKNOWN"
}

export enum OrderState {
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE"
}

export enum OrderStatus {
    WAITING_OPERATOR = "WAITING_OPERATOR",
    ON_MARKET = "ON_MARKET",
    DONE = "DONE"
}

export enum OrderLifetime {
    DATE = "DATE",
    END_OF_DAY = "END_OF_DAY",
    GOOD_TILL_CANCEL = "GOOD_TILL_CANCEL",
    FILL_AND_KILL = "FILL_AND_KILL",
    FILL_OR_KILL = "FILL_OR_KILL"
}

export function isTmcOrder(order: OrderRecord): order is TmcOrderRecord {
    return "legs" in order;
}

export function isOptionOrder(order: OrderRecord): boolean {
    return order.exchange === Exchange.AOM;
}

export function isEquityOrder(order: OrderRecord): boolean {
    if ((order.exchange === Exchange.ASX) || (order.exchange === Exchange.NSX)) {
        return true;
    } else {
        return (order.exchange === Exchange.CXA) && (order.code.length === 3);
    }
}

export function isWarrantOrder(order: OrderRecord): boolean {
    if (order.exchange === Exchange.AXW) {
        return true;
    } else {
        return (order.exchange === Exchange.CXA) && (order.code.length > 3);
    }
}

export function isOpen(order: OrderRecord): boolean {
    return (order.status === OrderStatus.WAITING_OPERATOR) || (order.status === OrderStatus.ON_MARKET);
}

export function isSameOrder(o1: OrderRecord, o2: OrderRecord): boolean {
    if (isIressOrder(o1) && isIressOrder(o2)) {
        return (o1.orderNumber === o2.orderNumber) && (o1.iosServer === o2.iosServer);
    } else if (isIressOrder(o1)) {
        return false; // different order types, can't be the same order
    } else {
        return o1.orderNumber === o2.orderNumber;
    }
}
